/**
 * 统一数据映射层 - 从数据库 snake_case 字段到前端标准 camelCase 格式
 * 
 * 试卷编辑页面（PaperEditor）是唯一数据来源，所有答题页面必须通过此映射层转换数据。
 * 确保所有页面的图文排列方式与 PaperEditor 保持一致。
 */

// 布局值映射：数据库值 → 前端标准值
const TITLE_LAYOUT_MAP: Record<string, string> = {
  'auto': 'auto',
  'cols1': 'row',
  'cols2': 'col-2',
  'cols3': 'col-3',
  'cols4': 'col-4',
  // 兼容已使用前端标准值的数据
  'row': 'row',
  'col-2': 'col-2',
  'col-3': 'col-3',
  'col-4': 'col-4',
};

/**
 * 将数据库原始题目数据映射为前端标准格式
 * 
 * @param dbQ - 数据库中的题目数据（snake_case 字段）
 * @returns 前端标准格式的题目数据（camelCase 字段）
 */
export function mapDbQuestionToStandard(dbQ: any): any {
  // 1. 合并 option_image_widths 全局映射到每个 Option 中
  const globalOptionWidths = dbQ.option_image_widths || {};

  const mappedOptions = (dbQ.options || []).map((opt: any) => {
    // 优先使用 Option 自身的 images/imageWidths（新格式）
    // 回退到 image/imageWidth（旧格式）
    // 最后回退到 globalOptionWidths[opt.id]（DB全局映射）

    const images = opt.images && opt.images.length > 0
      ? opt.images
      : (opt.image ? [opt.image] : []);

    const imageWidths = opt.imageWidths && opt.imageWidths.length > 0
      ? opt.imageWidths
      : (opt.imageWidth
        ? [opt.imageWidth]
        : (globalOptionWidths[opt.id] ? [globalOptionWidths[opt.id]] : []));

    return {
      id: opt.id || opt.key || '',
      label: opt.label || opt.id || '',
      text: opt.text || opt.content || '',
      isCorrect: opt.isCorrect || false,
      // 保留旧字段向后兼容
      image: opt.image || (images.length === 1 ? images[0] : undefined),
      imageWidth: opt.imageWidth || (imageWidths.length === 1 ? imageWidths[0] : undefined),
      // 新字段：多图支持
      images: images.length > 0 ? images : undefined,
      imageWidths: imageWidths.length > 0 ? imageWidths : undefined,
    };
  });

  // 2. 映射题干布局值
  const titleImageLayout = TITLE_LAYOUT_MAP[dbQ.title_image_layout || 'auto'] || 'auto';

  // 3. 构建标准 Question 对象
  return {
    id: dbQ.id,
    type: dbQ.question_type || dbQ.type || 'single_choice',
    subType: dbQ.sub_type || dbQ.subType,
    // 题目名称（非客观题专用）：取 dbQ.name（数据库中的题目名称字段），回退到 dbQ.question_name 和 dbQ.title
    questionTitle: dbQ.name ?? dbQ.question_name ?? dbQ.title ?? '',
    // 题干内容：优先取 dbQ.content（富文本题干），回退到 dbQ.title（兼容旧数据）
    questionStem: dbQ.content || dbQ.title || '',
    options: mappedOptions,
    score: dbQ.score || 0,
    titleImages: dbQ.title_images || dbQ.titleImages || [],
    titleImageWidths: dbQ.title_image_widths || dbQ.titleImageWidths || [],
    titleImageLayout: titleImageLayout,
    imagePosition: dbQ.image_position || dbQ.imagePosition || 'below',
    optionLayout: dbQ.option_layout || dbQ.optionLayout || 'grid',
    optionImageWidths: globalOptionWidths,
    explanation: dbQ.explanation || '',
    answerText: dbQ.reference_answer || dbQ.answerText || '',
    scoringPoints: dbQ.scoring_points || dbQ.scoringPoints || '',
    codeTemplate: dbQ.code_template || dbQ.codeTemplate || '',
    codeLanguage: dbQ.code_language || dbQ.codeLanguage || '',
    allowTextInput: dbQ.allow_text_input ?? dbQ.allowTextInput ?? true,
    allowImageUpload: dbQ.allow_image_upload ?? dbQ.allowImageUpload ?? false,
    allowFileUpload: dbQ.allow_file_upload ?? dbQ.allowFileUpload ?? false,
    maxTextLength: dbQ.max_text_length || dbQ.maxTextLength || 800,
    maxImageSize: dbQ.max_image_size || dbQ.maxImageSize || 0,
    maxFileSize: dbQ.max_file_size || dbQ.maxFileSize || 0,
    scratchAnswerItems: dbQ.scratch_answer_items || dbQ.scratchAnswerItems,
    graphicsContent: dbQ.graphics_content || dbQ.graphicsContent,
    graphicsDifficulty: dbQ.graphics_difficulty || dbQ.graphicsDifficulty,
    graphicsCategory: dbQ.graphics_category || dbQ.graphicsCategory,
    graphicsAnswerItems: dbQ.graphics_answer_items || dbQ.graphicsAnswerItems,
  };
}

/**
 * 根据布局值和图片数量计算 grid 列数
 *
 * @param layout - 布局值：'row'(单行) | 'col-2'(2列) | 'col-3'(3列) | 'auto'(自动)
 * @param imageCount - 图片数量
 * @returns grid 列数
 */
export function getTitleImageGridCols(layout: string, imageCount: number): number {
  if (imageCount <= 1) return 1;
  switch (layout) {
    case 'row': return imageCount; // 所有图片一行
    case 'col-2': return Math.min(2, imageCount);
    case 'col-3': return Math.min(3, imageCount);
    case 'auto':
    default:
      // 自动：2张及以下用2列，3张及以上用3列
      return imageCount <= 2 ? Math.min(2, imageCount) : Math.min(3, imageCount);
  }
}

/**
 * 批量映射数据库题目数据为前端标准格式
 */
export function mapDbQuestionsToStandard(dbQuestions: any[]): any[] {
  return dbQuestions.map(mapDbQuestionToStandard);
}

/**
 * 从数据库补充布局/缩放字段到已有的题目数据
 * 
 * 用于 Quiz.tsx 等页面，这些页面从答题记录中获取题目数据，
 * 但答题记录中可能缺少布局/缩放字段，需要从数据库补充。
 */
export function enrichQuestionsWithDbFields(questions: any[], dbFields: any[]): any[] {
  const questionMap = new Map(dbFields.map((q: any) => [q.id, q]));

  return questions.map((q: any) => {
    const dbQ = questionMap.get(q.id);
    if (!dbQ) return q;

    const globalOptionWidths = dbQ.option_image_widths || {};
    const mappedOptions = (q.options || []).map((opt: any) => ({
      ...opt,
      // 合并数据库中的全局选项图片宽度
      imageWidth: globalOptionWidths[opt.id] || opt.imageWidth || 100,
      // 同时保留多图支持
      images: opt.images || (opt.image ? [opt.image] : undefined),
      imageWidths: opt.imageWidths || (opt.imageWidth ? [opt.imageWidth] : undefined),
    }));

    return {
      ...q,
      questionTitle: dbQ.name ?? dbQ.question_name ?? dbQ.title ?? q.questionTitle ?? '',
      questionStem: dbQ.content || dbQ.title || q.questionStem || '',
      titleImageLayout: TITLE_LAYOUT_MAP[dbQ.title_image_layout || 'auto'] || q.titleImageLayout || 'auto',
      titleImageWidths: dbQ.title_image_widths || q.titleImageWidths || [],
      titleImages: dbQ.title_images || q.titleImages || [],
      imagePosition: dbQ.image_position || q.imagePosition || 'below',
      optionLayout: dbQ.option_layout || q.optionLayout || 'grid',
      options: mappedOptions,
    };
  });
}